import { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useDebounce } from "@/hooks/useDebounce";
import { DateRange } from "react-day-picker";

export interface Receipt {
  id: string;
  merchant_name: string | null;
  category: string;
  amount: number;
  date: string;
  file_path: string;
  subtotal: number;
  tax: number;
  total: number;
  currency: string;
  parsed_json?: any;
  line_items?: any[];
  status: string;
  confidence_score?: number;
  raw_ocr_text?: string;
  manual_total?: number;
  version?: number;
  original_file_path?: string;
  confidence_regions?: any;
}

export interface ReceiptFilters {
  searchQuery: string;
  category: string;
  dateRange: DateRange | undefined;
  confidenceLevel: string;
  expenseType: string;
}

const PAGE_SIZE = 50;

export function useReceipts() {
  const { toast } = useToast();
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [filters, setFilters] = useState<ReceiptFilters>({
    searchQuery: "",
    category: "All Categories",
    dateRange: undefined,
    confidenceLevel: "all",
    expenseType: "all",
  });

  const debouncedSearchQuery = useDebounce(filters.searchQuery, 300);

  const queryFilters = useMemo(() => ({
    searchQuery: debouncedSearchQuery,
    category: filters.category,
    dateRange: filters.dateRange,
    confidenceLevel: filters.confidenceLevel,
    expenseType: filters.expenseType,
  }), [debouncedSearchQuery, filters.category, filters.dateRange, filters.confidenceLevel, filters.expenseType]);

  const fetchReceipts = useCallback(async (reset = false) => {
    try {
      if (reset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const currentPage = reset ? 0 : page;
      const start = currentPage * PAGE_SIZE;
      const end = start + PAGE_SIZE - 1;

      let query = supabase
        .from("receipts")
        .select("*", { count: 'exact' })
        .eq("user_id", user.id);

      if (filters.searchQuery) {
        query = query.ilike("merchant_name", `%${filters.searchQuery}%`);
      }

      if (filters.category !== "All Categories") {
        query = query.eq("category", filters.category);
      }

      if (filters.dateRange?.from) {
        query = query.gte("date", filters.dateRange.from.toISOString().split('T')[0]);
      }
      if (filters.dateRange?.to) {
        query = query.lte("date", filters.dateRange.to.toISOString().split('T')[0]);
      }

      switch (filters.confidenceLevel) {
        case "high":
          query = query.gte("confidence_score", 0.8);
          break;
        case "medium":
          query = query.gte("confidence_score", 0.6).lt("confidence_score", 0.8);
          break;
        case "low":
          query = query.lt("confidence_score", 0.6);
          break;
      }

      if (filters.expenseType === "business") {
        query = query.eq("status", "business");
      }
      if (filters.expenseType === "personal") {
        query = query.eq("status", "personal");
      }
      if (filters.expenseType === "deductible") {
        query = query.gt("tax_savings_estimate", 0);
      }

      query = query
        .order("date", { ascending: false })
        .range(start, end);

      const { data, error } = await query;

      if (error) throw error;
      
      const newReceipts = (data || []) as Receipt[];
      setReceipts(reset ? newReceipts : [...receipts, ...newReceipts]);
      setHasMore(newReceipts.length === PAGE_SIZE);
      if (!reset) {
        setPage(currentPage + 1);
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [page, filters, receipts, toast]);

  useEffect(() => {
    setPage(0);
    setReceipts([]);
    fetchReceipts(true);
  }, [queryFilters]);

  const resetFilters = useCallback(() => {
    setFilters({
      searchQuery: "",
      category: "All Categories",
      dateRange: undefined,
      confidenceLevel: "all",
      expenseType: "all",
    });
  }, []);

  return {
    receipts,
    loading,
    loadingMore,
    hasMore,
    filters,
    setFilters,
    resetFilters,
    fetchReceipts,
    refetch: () => fetchReceipts(true),
  };
}
